import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@forgenetwork/ui';
import { InvoiceList, PaymentMethodCard, UpgradeBanner } from '@/components/billing';
import { useToast } from '@/shared/hooks/useToast';
import { api } from '@/lib/api';
import { CreditCard, Calendar, ExternalLink, Zap } from 'lucide-react';
import type { Invoice, PaymentMethod } from '@/components/billing';

export const Route = createFileRoute('/_authenticated/billing')({
  component: BillingPage,
});

interface Subscription {
  id: string;
  status: 'active' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'paused';
  planId: string;
  planName?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Customer {
  id: string;
  stripeCustomerId: string;
  createdAt: string;
  updatedAt: string;
}

interface Plan {
  id: string;
  name: string;
  tier: string;
  description: string | null;
  price: number;
  stripePriceId: string | null;
  features: string[];
  maxRepositories: number | null;
  maxWorkspaces: number | null;
  maxTeamMembers: number | null;
}

function BillingPage(): React.JSX.Element {
  const { toast } = useToast();
  const [upgradeBannerDismissed, setUpgradeBannerDismissed] = React.useState(false);

  // Fetch subscription
  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery<{
    subscription: Subscription | null;
    message?: string;
  }>({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => api.get('/billing/subscription'),
  });

  // Fetch customer
  const { data: customerData } = useQuery<{ customer: Customer | null }>({
    queryKey: ['billing', 'customer'],
    queryFn: async () => api.get('/billing/customer'),
  });

  // Fetch invoices
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery<{
    invoices: Invoice[];
    count: number;
  }>({
    queryKey: ['billing', 'invoices'],
    queryFn: async () => api.get('/billing/invoices?limit=20'),
  });

  // Fetch payment method
  const { data: paymentMethodData, isLoading: paymentMethodLoading } = useQuery<{
    paymentMethod: PaymentMethod | null;
    message?: string;
  }>({
    queryKey: ['billing', 'payment-method'],
    queryFn: async () => api.get('/billing/payment-method'),
  });

  // Fetch available plans
  const { data: plansData } = useQuery<{ plans: Plan[] }>({
    queryKey: ['billing', 'plans'],
    queryFn: async () => api.get('/billing/plans'),
  });

  const paymentMethod = paymentMethodData?.paymentMethod ?? null;

  // Find the Pro plan's stripe price ID
  const proPlan = plansData?.plans?.find((p) => p.tier === 'pro');
  const proPriceId = proPlan?.stripePriceId || 'price_pro'; // Fallback to placeholder

  // Create billing portal session mutation
  const billingPortalMutation = useMutation({
    mutationFn: async () => {
      return api.post<{ url: string }>('/billing/portal', {
        returnUrl: window.location.href,
      });
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to open billing portal',
        variant: 'destructive',
      });
    },
  });

  // Create checkout session mutation (for upgrading to Pro)
  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      return api.post<{ url: string }>('/billing/checkout', {
        priceId,
        successUrl: `${window.location.origin}/billing?checkout=success`,
        cancelUrl: `${window.location.origin}/billing?checkout=cancelled`,
      });
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start checkout',
        variant: 'destructive',
      });
    },
  });

  const subscription = subscriptionData?.subscription;
  const hasActiveSubscription = subscription && ['active', 'trialing'].includes(subscription.status);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: Subscription['status']): React.JSX.Element => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'trialing':
        return <Badge variant="secondary">Trial</Badge>;
      case 'past_due':
        return <Badge variant="warning">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="outline">Canceled</Badge>;
      case 'unpaid':
        return <Badge variant="destructive">Unpaid</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPlanDisplayName = (sub: Subscription): string => {
    // Use API-provided plan name if available
    if (sub.planName) {
      const name = sub.planName.toLowerCase();
      if (name === 'pro') return 'Pro';
      if (name === 'enterprise') return 'Enterprise';
      if (name === 'free' || name === 'starter') return 'Starter';
      // Capitalize first letter for unknown plan names
      return sub.planName.charAt(0).toUpperCase() + sub.planName.slice(1);
    }
    // Fallback to parsing planId
    if (sub.planId.includes('pro')) return 'Pro';
    if (sub.planId.includes('enterprise')) return 'Enterprise';
    return 'Starter';
  };

  if (subscriptionLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8 text-muted-foreground">Loading billing information...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Billing</h1>
          <p className="text-muted-foreground">
            Manage your subscription, payment methods, and invoices
          </p>
        </div>

        {/* Upgrade Banner (only show on free tier if not dismissed) */}
        {!hasActiveSubscription && !upgradeBannerDismissed && (
          <UpgradeBanner
            feature="Advanced features and unlimited repositories"
            targetPlan="Pro"
            onDismiss={() => setUpgradeBannerDismissed(true)}
          />
        )}

        {/* Current Plan */}
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>Your active subscription and plan details</CardDescription>
          </CardHeader>
          <CardContent>
            {subscription ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/60">
                      <Zap className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="font-semibold text-lg">{getPlanDisplayName(subscription)}</div>
                      <div className="text-sm text-muted-foreground">
                        {subscription.cancelAtPeriodEnd ? (
                          <>Cancels on {formatDate(subscription.currentPeriodEnd)}</>
                        ) : (
                          <>Renews on {formatDate(subscription.currentPeriodEnd)}</>
                        )}
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(subscription.status)}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Calendar className="h-4 w-4" />
                      Current Period
                    </div>
                    <div className="text-sm font-medium">
                      {formatDate(subscription.currentPeriodStart)} -{' '}
                      {formatDate(subscription.currentPeriodEnd)}
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <CreditCard className="h-4 w-4" />
                      Subscription ID
                    </div>
                    <div className="text-sm font-medium font-mono">
                      {subscription.id.substring(0, 12)}...
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={() => billingPortalMutation.mutate()}
                  disabled={billingPortalMutation.isPending}
                  className="w-full"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {billingPortalMutation.isPending ? 'Opening...' : 'Manage Subscription'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <div className="font-semibold">Starter Plan</div>
                    <div className="text-sm text-muted-foreground">
                      Limited features with basic functionality
                    </div>
                  </div>
                  <Badge variant="outline">Free</Badge>
                </div>

                <Button
                  className="w-full"
                  onClick={() => checkoutMutation.mutate(proPriceId)}
                  disabled={checkoutMutation.isPending}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  {checkoutMutation.isPending ? 'Starting checkout...' : 'Upgrade to Pro'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Method (only show if has subscription) */}
        {hasActiveSubscription && (
          <PaymentMethodCard paymentMethod={paymentMethod} isLoading={paymentMethodLoading} />
        )}

        {/* Invoice History */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice History</CardTitle>
            <CardDescription>View and download your billing invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <InvoiceList
              invoices={invoicesData?.invoices || []}
              isLoading={invoicesLoading}
            />
          </CardContent>
        </Card>

        {/* Billing Information */}
        {customerData?.customer && (
          <Card>
            <CardHeader>
              <CardTitle>Billing Information</CardTitle>
              <CardDescription>Your billing account details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Customer ID</span>
                  <span className="font-mono">{customerData.customer.stripeCustomerId}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Account Created</span>
                  <span>{formatDate(customerData.customer.createdAt)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
